import { useEffect, useState } from 'react'
import { Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { Drawer } from '../common/overlays/Drawer'
import { IconButton } from '../common/primitives/IconButton'
import { ProductNavigation } from './ProductNavigation'
import styles from './Layout.module.css'

const COMPACT_SHELL_QUERY = '(max-width: 64rem)'

function useCompactShell() {
  const [compact, setCompact] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia?.(COMPACT_SHELL_QUERY).matches
  ))

  useEffect(() => {
    const media = window.matchMedia?.(COMPACT_SHELL_QUERY)
    if (!media) return undefined
    const update = () => setCompact(media.matches)
    update()
    media.addEventListener?.('change', update)
    return () => media.removeEventListener?.('change', update)
  }, [])

  return compact
}

export function AppShell({ brand, organizationContext, productSwitcher, navigation, mobileNavigation, navigationItems, navigationLabel = 'Tasks', navigationCollapsed = false, onNavigationCollapsedChange, globalActions, mobileNavigationHeader, mobileNavigationFooter, mobileOpen = false, onMobileOpenChange, children, mainId = 'studio-main', className = '' }) {
  const compactShell = useCompactShell()
  const taskNavigation = navigation || <ProductNavigation items={navigationItems} label={navigationLabel} collapsed={navigationCollapsed} />
  const drawerNavigation = mobileNavigation || navigation || <ProductNavigation items={navigationItems} label={navigationLabel} />
  return (
    <div className={`${styles.shell} ${className}`.trim()} data-testid="app-shell" data-navigation-mode={compactShell ? 'drawer' : 'rail'}>
      <a className={styles.skipLink} href={`#${mainId}`}>Skip to main content</a>
      <header className={styles.globalHeader}>
        <div className={styles.brand}>{brand}</div>
        <div className={styles.organization}>{organizationContext}</div>
        <div className={styles.globalActions}>{globalActions}</div>
        <IconButton
          className={styles.mobileTrigger}
          label={mobileOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={mobileOpen}
          hidden={!compactShell}
          onClick={() => onMobileOpenChange?.(!mobileOpen)}
        >
          <Menu size={20} aria-hidden="true" />
        </IconButton>
      </header>
      <div className={`${styles.shellBody} ${navigationCollapsed ? styles.shellBodyCollapsed : ''}`.trim()}>
        <aside
          className={`${styles.taskRail} ${navigationCollapsed ? styles.taskRailCollapsed : ''}`.trim()}
          aria-label={`${navigationLabel} sidebar`}
          data-collapsed={navigationCollapsed}
          hidden={compactShell}
        >
          {productSwitcher || onNavigationCollapsedChange ? (
            <div className={styles.railHeader}>
              {productSwitcher ? <div className={styles.railContext}>{productSwitcher}</div> : null}
              {onNavigationCollapsedChange ? (
                <IconButton
                  className={styles.railToggle}
                  variant="ghost"
                  label={navigationCollapsed ? 'Expand navigation' : 'Collapse navigation'}
                  aria-expanded={!navigationCollapsed}
                  onClick={() => onNavigationCollapsedChange(!navigationCollapsed)}
                >
                  {navigationCollapsed
                    ? <PanelLeftOpen size={18} aria-hidden="true" />
                    : <PanelLeftClose size={18} aria-hidden="true" />}
                </IconButton>
              ) : null}
            </div>
          ) : null}
          {taskNavigation}
        </aside>
        <main id={mainId} className={styles.main} tabIndex={-1} data-wf-focus-fallback>{children}</main>
      </div>
      <Drawer
        open={mobileOpen}
        title={navigationLabel}
        footer={mobileNavigationFooter}
        onClose={() => onMobileOpenChange?.(false)}
      >
        {mobileNavigationHeader}
        {drawerNavigation}
      </Drawer>
    </div>
  )
}
